import React, { useState, useMemo, useRef, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { motion, AnimatePresence } from 'framer-motion';
import {
  User, Building, Phone, Mail, Plus, Search, Filter, X, History,
  MessageSquare, PhoneCall, Mail as MailIcon, StickyNote, Users,
  Upload, FileUp, Download, MessageCircle, MapPin, FileText,
  ChevronLeft, ChevronRight, UserCheck, TrendingUp, Check, Edit,
  Trash2, Info, MapPinned, Clock, Send,
  Building2, Globe, CreditCard, Percent, Tag, Hash,
} from 'lucide-react';
import { getStatusColor, parseFieldErrors } from '../utils/helpers';
import EditableField from './EditableField';
import ContactImporter from './ContactImporter';
import { format, parseISO, startOfWeek, endOfWeek, isWithinInterval } from 'date-fns';
import * as XLSX from 'xlsx';
import { api } from '../config/api';
import { catalogService } from '../services/catalogService';
import { categoryService } from '../services/categoryService';

const INTERACTION_TYPES = [
  { value: 'Llamada', label: 'Llamada', icon: PhoneCall, color: 'blue' },
  { value: 'WhatsApp', label: 'WhatsApp', icon: MessageCircle, color: 'green' },
  { value: 'Visita', label: 'Visita', icon: MapPin, color: 'purple' },
  { value: 'Reunión', label: 'Reunión', icon: Users, color: 'orange' },
  { value: 'Email', label: 'Email', icon: MailIcon, color: 'indigo' },
  { value: 'Nota interna', label: 'Nota interna', icon: FileText, color: 'gray' },
];

const CONTACT_TYPE_OPTIONS = [
  { value: 'CLIENT', label: 'Cliente' },
  { value: 'SUPPLIER', label: 'Agente' },
  { value: 'FACTORY', label: 'Fábrica' },
];

const SUPPLIER_CURRENCY_OPTIONS = ['USD', 'CNY'];

const ADDRESS_TYPES = [
  { value: 'BILLING', label: 'Facturación' },
  { value: 'DELIVERY', label: 'Entrega' },
  { value: 'OTHER', label: 'Otra dirección' },
  { value: 'TRACKING', label: 'Seguimiento' },
];

const getTabsForType = (contactType) => {
  const base = [
    { id: 'general', label: 'General', icon: User },
  ];
  if (contactType === 'CLIENT' || contactType === 'SUPPLIER') {
    base.push({ id: 'otra', label: 'Otra información', icon: Info });
  }
  // Historial oculto por ahora (ver renderHistorialTab más abajo para reactivarlo)
  return base;
};

const emptyAddress = (type = 'BILLING') => ({
  addressType: type,
  name: '',
  street: '',
  street2: '',
  city: '',
  state: '',
  country: 'Argentina',
  postalCode: '',
  email: '',
  phone: '',
  mobile: '',
  wechatId: '',
  qq: '',
  category: '',
});

const INITIAL_FORM = {
  contactType: 'CLIENT',
  name: '',
  fantasyName: '',
  position: '',
  area: '',
  email: '',
  phone: '',
  mobile: '',
  postalCode: '',
  comprador: '',
  codigoContacto: '',
  // General: ubicación y datos adicionales
  address: '',
  city: '',
  stateProvince: '',
  country: '',
  categories: '',
  wechatId: '',
  website: '',
  description: '',
  relationshipStartDate: '',
  // Agente vinculado (solo para Fábrica)
  agentId: '',
  // Puertos
  factoryPortId: '',
  destinationPortIds: [],
  loadPortId: '',
  destinationPortText: '',
  // Financiero
  paymentMethodId: '',
  creditDaysId: '',
  balancePaymentId: '',
  advancePaymentId: '',
  paymentTerm: '',
  beneficiaryAccountName: '',
  commissionPercentage: '',
  supplierCurrencies: [],
  beneficiaryBankAddress: '',
  swiftBic: '',
  routingAba: '',
  receiptReminder: '',
  labelType: '',
  taxInformation: '',
  discountPercentage: '',
  discountListId: '',
  kam: '',
  salesTeam: '',
  priceList: '',
  empresaNombre: '',
  reference: '',
  sector: '',
  idType: '',
  idNumber: '',
  bank: '',
  accountNumber: '',
  // Info de envío
  notifyParty: '',
  consignee: '',
  transitId: '',
  requirements: '',
  shippingInstructions: '',
  // Otra información
  storeNumber: '',
  storeFormat: '',
  fairs: '',
  // Estado/Provincia (Agente y Fábrica)
  stateId: '',
  // Dirección de facturación
  billingAddress: '',
  // Personas de contacto
  contactPersons: [],
  // Relaciones
  companyIds: [],
  status: 'Borrador',
  comments: '',
  addresses: [],
};

const INPUT_CLASS = 'w-full h-10 px-3 text-sm bg-white border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500 transition-colors';
const TEXTAREA_CLASS = 'w-full px-3 py-2 text-sm bg-white border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500 transition-colors resize-none';

// ─── Constantes estables fuera del componente para evitar recreación en cada render ─

const STATUS_OPTIONS = [
  { value: 'Borrador', label: 'Borrador' },
  { value: 'Activo', label: 'Activo' },
  { value: 'Inactivo', label: 'Inactivo' },
];

const FIELD_TO_TAB = {
  // General
  name: 'general', fantasy_name: 'general', email: 'general', phone: 'general',
  mobile: 'general', position: 'general', area: 'general', comprador: 'general',
  categories: 'general', website: 'general', address: 'general', billing_address: 'general',
  city: 'general', state_province: 'general', country: 'general', wechat_id: 'general',
  relationship_start_date: 'general', label_type: 'general', description: 'general',
  status: 'general', codigo_contacto: 'general', contact_type: 'general',
  agent_id: 'general', factory_port_id: 'general', state_id: 'general',
  kam: 'general',
  notify_party: 'general', consignee: 'general', transit_id: 'general',
  destination_port_text: 'general', requirements: 'general',
  shipping_instructions: 'general', load_port_id: 'general', destination_port_ids: 'general',
  payment_method: 'general', credit_days: 'general',
  balance_payment: 'general', advance_payment: 'general',
  payment_term: 'general', beneficiary_account_name: 'general',
  commission_percentage: 'general', supplier_currencies: 'general',
  beneficiary_bank_address: 'general', swift_bic: 'general',
  routing_aba: 'general', receipt_reminder: 'general',
  tax_information: 'general', discount_percentage: 'general', discount_list: 'general',
  empresa_nombre: 'general', sales_team: 'general', price_list: 'general',
  reference: 'general', sector: 'general', bank: 'general', account_number: 'general',
  id_type: 'general', id_number: 'general',
  // Otra información
  store_number: 'otra', store_format: 'otra', fairs: 'otra',
};

const FormInput = ({ label, required, error, ...props }) => (
  <div>
    <label className="block text-xs font-medium text-gray-500 mb-1">
      {label} {required && <span className="text-red-500">*</span>}
    </label>
    <input {...props} className={error ? `${INPUT_CLASS} border-red-400 focus:border-red-500 focus:ring-red-500/30` : INPUT_CLASS} />
    {error && <p className="mt-1 text-xs text-red-600">{error}</p>}
  </div>
);

const FormSelect = ({ label, required, error, children, ...props }) => (
  <div>
    <label className="block text-xs font-medium text-gray-500 mb-1">
      {label} {required && <span className="text-red-500">*</span>}
    </label>
    <select {...props} className={error ? `${INPUT_CLASS} border-red-400 focus:border-red-500 focus:ring-red-500/30` : INPUT_CLASS}>{children}</select>
    {error && <p className="mt-1 text-xs text-red-600">{error}</p>}
  </div>
);

const FormTextarea = ({ label, required, error, ...props }) => (
  <div>
    <label className="block text-xs font-medium text-gray-500 mb-1">
      {label} {required && <span className="text-red-500">*</span>}
    </label>
    <textarea {...props} className={error ? `${TEXTAREA_CLASS} border-red-400 focus:border-red-500 focus:ring-red-500/30` : TEXTAREA_CLASS} />
    {error && <p className="mt-1 text-xs text-red-600">{error}</p>}
  </div>
);

const SectionDivider = ({ label }) => (
  <div className="flex items-center gap-3 mt-2 mb-3">
    <div className="flex-1 h-0.5 bg-black" />
    <span className="text-xs font-bold text-black uppercase tracking-wide">{label}</span>
    <div className="flex-1 h-0.5 bg-black" />
  </div>
);

// ─── Tabla de contactos con columnas configurables ───────────────────────────

const ALL_COLUMNS = [
  { key: 'codigo',     label: 'Código',     defaultVisible: true  },
  { key: 'contacto',   label: 'Contacto',   defaultVisible: true  },
  { key: 'area',       label: 'Área',       defaultVisible: true  },
  { key: 'puesto',     label: 'Puesto',     defaultVisible: true  },
  { key: 'email',      label: 'Email',      defaultVisible: true  },
  { key: 'telefono',   label: 'Teléfono',   defaultVisible: true  },
  { key: 'wechat',     label: 'WeChat',     defaultVisible: true  },
  { key: 'categorias', label: 'Categorías', defaultVisible: true  },
  { key: 'tipo',       label: 'Tipo',       defaultVisible: true  },
  { key: 'empresa',    label: 'Empresa',    defaultVisible: true  },
  { key: 'estado',     label: 'Estado',     defaultVisible: true  },
];

const ContactTable = ({ contacts, onEdit, onDelete }) => {
  const { user } = useAuth();
  const storageKey = `contactTableCols_${user?.id || 'default'}`;

  const [colOrder, setColOrder] = useState(() => {
    try {
      const saved = JSON.parse(localStorage.getItem(storageKey));
      if (saved?.order && Array.isArray(saved.order)) return saved.order;
    } catch {}
    return ALL_COLUMNS.map(c => c.key);
  });

  const [colVis, setColVis] = useState(() => {
    try {
      const saved = JSON.parse(localStorage.getItem(storageKey));
      if (saved?.visibility && typeof saved.visibility === 'object') return saved.visibility;
    } catch {}
    return Object.fromEntries(ALL_COLUMNS.map(c => [c.key, c.defaultVisible]));
  });

  // Persistir cada vez que cambia el orden o visibilidad
  useEffect(() => {
    try {
      localStorage.setItem(storageKey, JSON.stringify({ order: colOrder, visibility: colVis }));
    } catch {}
  }, [colOrder, colVis, storageKey]);

  const [showColPanel, setShowColPanel] = useState(false);
  const dragKey = useRef(null);
  const panelRef = useRef(null);

  // Cerrar panel al hacer clic afuera
  useEffect(() => {
    const handler = (e) => {
      if (panelRef.current && !panelRef.current.contains(e.target)) setShowColPanel(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const visibleCols = colOrder.filter(k => colVis[k]);

  const toggleCol = (key) => {
    const currentVisible = Object.values(colVis).filter(Boolean).length;
    if (colVis[key] && currentVisible <= 1) return; // al menos 1 col visible
    setColVis(prev => ({ ...prev, [key]: !prev[key] }));
  };

  // Drag & drop en el panel
  const onDragStartPanel = (key) => { dragKey.current = key; };
  const onDropPanel = (targetKey) => {
    if (!dragKey.current || dragKey.current === targetKey) return;
    setColOrder(prev => {
      const arr = [...prev];
      const from = arr.indexOf(dragKey.current);
      const to   = arr.indexOf(targetKey);
      arr.splice(from, 1);
      arr.splice(to, 0, dragKey.current);
      return arr;
    });
    dragKey.current = null;
  };

  // Drag & drop en los headers de la tabla
  const onDragStartHeader = (key) => { dragKey.current = key; };
  const onDropHeader = (targetKey) => {
    if (!dragKey.current || dragKey.current === targetKey) return;
    setColOrder(prev => {
      const arr = [...prev];
      const from = arr.indexOf(dragKey.current);
      const to   = arr.indexOf(targetKey);
      arr.splice(from, 1);
      arr.splice(to, 0, dragKey.current);
      return arr;
    });
    dragKey.current = null;
  };

  const renderCell = (col, contact) => {
    const companyNames = (contact.companies || []).map(c => c.name).join(', ');
    const typeLabel = CONTACT_TYPE_OPTIONS.find(o => o.value === contact.contactType)?.label || 'Cliente';
    switch (col) {
      case 'codigo':
        return <td key={col} className="px-3 py-3 text-xs font-mono text-gray-500">{contact.codigoContacto || '-'}</td>;
      case 'contacto':
        return <td key={col} className="px-3 py-3"><div className="text-sm font-medium text-gray-900">{contact.name}</div></td>;
      case 'area':
        return <td key={col} className="px-3 py-3 text-sm text-gray-600">{contact.area || '-'}</td>;
      case 'puesto':
        return <td key={col} className="px-3 py-3 text-sm text-gray-600">{contact.position || '-'}</td>;
      case 'email':
        return <td key={col} className="px-3 py-3 text-sm text-gray-600">{contact.email || '-'}</td>;
      case 'telefono':
        return <td key={col} className="px-3 py-3 text-sm text-gray-600">{contact.phone || '-'}</td>;
      case 'wechat':
        return <td key={col} className="px-3 py-3 text-sm text-gray-600">{contact.wechatId || '-'}</td>;
      case 'categorias':
        return <td key={col} className="px-3 py-3 text-sm text-gray-600">{contact.categories || '-'}</td>;
      case 'tipo':
        return (
          <td key={col} className="px-3 py-3 text-center">
            <span className={`inline-flex px-2 py-0.5 rounded text-xs font-medium ${
              contact.contactType === 'FACTORY'  ? 'bg-orange-100 text-orange-700' :
              contact.contactType === 'SUPPLIER' ? 'bg-teal-100 text-teal-700' :
              'bg-blue-100 text-blue-700'
            }`}>{typeLabel}</span>
          </td>
        );
      case 'empresa':
        return (
          <td key={col} className="px-3 py-3 text-sm text-gray-600 max-w-[150px] truncate" title={companyNames}>
            {companyNames || '-'}
          </td>
        );
      case 'estado':
        return (
          <td key={col} className="px-3 py-3 text-center">
            {contact.contactType === 'CLIENT' ? (
              <span className={`inline-flex px-2 py-0.5 rounded text-xs font-medium ${getStatusColor(contact.status)}`}>
                {contact.status}
              </span>
            ) : (
              <span className="text-xs text-gray-300">—</span>
            )}
          </td>
        );
      default:
        return <td key={col} />;
    }
  };

  return (
    <div className="bg-white border border-gray-200 rounded-xl shadow-sm">
      {/* Header de la tabla */}
      <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between">
        <h2 className="text-sm font-semibold text-gray-700">Contactos ({contacts.length})</h2>
        <div className="relative" ref={panelRef}>
          <button
            onClick={() => setShowColPanel(v => !v)}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-gray-600 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            title="Configurar columnas"
          >
            <Filter className="w-3.5 h-3.5" />
            Columnas
          </button>

          {showColPanel && (
            <div className="absolute right-0 top-9 z-[9999] bg-white border border-gray-200 rounded-xl shadow-xl w-56 py-2">
              <p className="px-3 pb-2 text-xs font-semibold text-gray-500 uppercase border-b border-gray-100 mb-1">
                Mostrar / Ocultar · Arrastrar para ordenar
              </p>
              {colOrder.map(key => {
                const col = ALL_COLUMNS.find(c => c.key === key);
                return (
                  <div
                    key={key}
                    draggable
                    onDragStart={() => onDragStartPanel(key)}
                    onDragOver={(e) => e.preventDefault()}
                    onDrop={() => onDropPanel(key)}
                    className="flex items-center gap-2 px-3 py-2 hover:bg-gray-50 cursor-grab active:cursor-grabbing select-none"
                  >
                    {/* grip */}
                    <span className="text-gray-300 text-xs">⠿</span>
                    <label className="flex items-center gap-2 flex-1 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={!!colVis[key]}
                        onChange={() => toggleCol(key)}
                        className="w-3.5 h-3.5 rounded border-gray-300 text-blue-600"
                      />
                      <span className="text-sm text-gray-700">{col?.label}</span>
                    </label>
                  </div>
                );
              })}
              <div className="border-t border-gray-100 mt-1 px-3 pt-2 flex gap-2">
                <button onClick={() => setColVis(Object.fromEntries(ALL_COLUMNS.map(c => [c.key, true])))}
                  className="text-xs text-blue-600 hover:text-blue-800">Ver todas</button>
                <span className="text-gray-300">|</span>
                <button onClick={() => setColOrder(ALL_COLUMNS.map(c => c.key))}
                  className="text-xs text-gray-500 hover:text-gray-700">Restablecer orden</button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Tabla */}
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr>
              {visibleCols.map(key => {
                const col = ALL_COLUMNS.find(c => c.key === key);
                const centered = ['tipo', 'estado'].includes(key);
                return (
                  <th
                    key={key}
                    draggable
                    onDragStart={() => onDragStartHeader(key)}
                    onDragOver={(e) => e.preventDefault()}
                    onDrop={() => onDropHeader(key)}
                    className={`px-3 py-3 text-xs font-medium text-gray-500 uppercase cursor-grab active:cursor-grabbing select-none group ${centered ? 'text-center' : 'text-left'}`}
                    title="Arrastrar para mover la columna"
                  >
                    <span className="inline-flex items-center gap-1">
                      <span className="opacity-0 group-hover:opacity-40 text-gray-400 text-xs">⠿</span>
                      {col?.label}
                    </span>
                  </th>
                );
              })}
              <th className="px-3 py-3 text-center text-xs font-medium text-gray-500 uppercase w-20">Acciones</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {contacts.map((contact) => (
              <tr
                key={contact.id}
                className="hover:bg-blue-50/50 transition-colors cursor-pointer"
                onClick={() => onEdit(contact)}
              >
                {visibleCols.map(key => renderCell(key, contact))}
                <td className="px-3 py-3 text-center">
                  <div className="flex items-center justify-center gap-1">
                    <button onClick={(e) => { e.stopPropagation(); onEdit(contact); }}
                      className="p-1.5 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors" title="Editar">
                      <Edit className="w-4 h-4" />
                    </button>
                    <button onClick={(e) => { e.stopPropagation(); onDelete(contact.id); }}
                      className="p-1.5 text-red-600 hover:bg-red-50 rounded-lg transition-colors" title="Eliminar">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────

const ContactManager = ({
  contacts = [],
  companies = [],
  contactToExpand = null,
  contactToEdit = null,
  onContactEditHandled = () => {},
  contactInteractions = [],
  onAddContact = () => {},
  onUpdateContact = () => {},
  onDeleteContact = () => {},
  onAddInteraction = () => {},
  onImportContacts = () => {},
}) => {
  const [showForm, setShowForm] = useState(false);
  const [showImporter, setShowImporter] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [companyFilter, setCompanyFilter] = useState('all');
  const [ports, setPorts] = useState([]);
  const [tagOptions, setTagOptions] = useState([]);
  const [clientTypeOptions, setClientTypeOptions] = useState([]);
  const [priceListOptions, setPriceListOptions] = useState([]);
  const [discountListOptions, setDiscountListOptions] = useState([]);
  const [countryOptions, setCountryOptions] = useState([]);
  const [stateOptions, setStateOptions] = useState([]);
  const [storeFormatOptions, setStoreFormatOptions] = useState([]);
  const [fairOptions, setFairOptions] = useState([]);
  const [categoryOptions, setCategoryOptions] = useState([]);
  const [salespersonOptions, setSalespersonOptions] = useState([]);
  const [addressTypeOptions, setAddressTypeOptions] = useState([]);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [creditDaysOptions, setCreditDaysOptions] = useState([]);
  const [balancePayments, setBalancePayments] = useState([]);
  const [advancePayments, setAdvancePayments] = useState([]);
  const [formErrors, setFormErrors] = useState({});
  const [successMessage, setSuccessMessage] = useState('');
  const [formActiveTab, setFormActiveTab] = useState('general');
  const [formData, setFormData] = useState({ ...INITIAL_FORM });
  const [editingContactId, setEditingContactId] = useState(null);

  // Company modal
  const [showCompanyModal, setShowCompanyModal] = useState(false);
  const [tempCompanyIds, setTempCompanyIds] = useState([]);
  const [companyModalSearch, setCompanyModalSearch] = useState('');
  const companyModalSearchRef = useRef(null);

  // Inline company creation
  const [showNewCompanyForm, setShowNewCompanyForm] = useState(false);
  const [newCompanyData, setNewCompanyData] = useState({ name: '', razonSocial: '', cuit: '', email: '', phone: '' });

  // Interaction
  const [showInteractionForm, setShowInteractionForm] = useState(null);
  const [selectedInteractionType, setSelectedInteractionType] = useState(null);
  const [interactionFormData, setInteractionFormData] = useState({
    type: 'Llamada', summary: '', date: format(new Date(), 'yyyy-MM-dd'), time: format(new Date(), 'HH:mm'),
  });

  const resetForm = useCallback(() => {
    setFormData({ ...INITIAL_FORM });
    setShowCompanyModal(false);
    setCompanyModalSearch('');
    setFormActiveTab('general');
    setEditingContactId(null);
    setShowNewCompanyForm(false);
    setNewCompanyData({ name: '', razonSocial: '', cuit: '', email: '', phone: '' });
    setFormErrors({});
  }, []);

  const loadContactIntoForm = useCallback((contact) => {
    if (!contact) return;
    const companyIds = Array.isArray(contact.companyIds) ? contact.companyIds
      : Array.isArray(contact.companies) ? contact.companies.map((c) => c.id) : [];
    const addresses = Array.isArray(contact.addresses) ? contact.addresses : [];

    setFormData({
      contactType: contact.contactType || 'CLIENT',
      name: contact.name || '',
      fantasyName: contact.fantasyName || '',
      position: contact.position || '',
      area: contact.area || '',
      email: contact.email || '',
      phone: contact.phone || '',
      mobile: contact.mobile || '',
      postalCode: contact.postalCode || '',
      comprador: contact.comprador || '',
      codigoContacto: contact.codigoContacto || '',
      address: contact.address || '',
      city: contact.city || '',
      stateProvince: contact.stateProvince || '',
      country: contact.country || '',
      categories: contact.categories || '',
      wechatId: contact.wechatId || '',
      website: contact.website || '',
      description: contact.description || '',
      relationshipStartDate: contact.relationshipStartDate || '',
      agentId: contact.agentId || '',
      factoryPortId: contact.factoryPortId || '',
      destinationPortIds: Array.isArray(contact.destinationPortIds) ? contact.destinationPortIds : [],
      loadPortId: contact.loadPortId || '',
      destinationPortText: contact.destinationPortText || '',
      paymentMethodId: contact.paymentMethodId || '',
      creditDaysId: contact.creditDaysId || '',
      balancePaymentId: contact.balancePaymentId || '',
      advancePaymentId: contact.advancePaymentId || '',
      paymentTerm: contact.paymentTerm || '',
      beneficiaryAccountName: contact.beneficiaryAccountName || '',
      commissionPercentage: contact.commissionPercentage ?? '',
      supplierCurrencies: Array.isArray(contact.supplierCurrencies) ? contact.supplierCurrencies : [],
      beneficiaryBankAddress: contact.beneficiaryBankAddress || '',
      swiftBic: contact.swiftBic || '',
      routingAba: contact.routingAba || '',
      receiptReminder: contact.receiptReminder || '',
      labelType: contact.labelType || '',
      taxInformation: contact.taxInformation || '',
      discountPercentage: contact.discountPercentage ?? '',
      discountListId: contact.discountListId || '',
      kam: contact.kam || '',
      salesTeam: contact.salesTeam || '',
      priceList: contact.priceList || '',
      empresaNombre: contact.empresaNombre || '',
      reference: contact.reference || '',
      sector: contact.sector || '',
      idType: contact.idType || '',
      idNumber: contact.idNumber || '',
      bank: contact.bank || '',
      accountNumber: contact.accountNumber || '',
      notifyParty: contact.notifyParty || '',
      consignee: contact.consignee || '',
      transitId: contact.transitId || '',
      requirements: contact.requirements || '',
      shippingInstructions: contact.shippingInstructions || '',
      storeNumber: contact.storeNumber || '',
      storeFormat: contact.storeFormat || '',
      fairs: contact.fairs || '',
      stateId: contact.stateId || '',
      billingAddress: contact.billingAddress || '',
      contactPersons: Array.isArray(contact.contactPersons) ? contact.contactPersons : [],
      companyIds,
      status: contact.status || 'Borrador',
      comments: contact.comments || '',
      addresses,
    });
    setEditingContactId(contact.id);
    setFormActiveTab('general');
    setShowForm(true);
  }, []);

  useEffect(() => {
    const loadPorts = async () => {
      try {
        const data = await api.get('/pricing/ports/');
        setPorts(Array.isArray(data) ? data : []);
      } catch { setPorts([]); }
    };
    const loadTags = async () => {
      try {
        const data = await catalogService.productTags.getAll({ active_only: true });
        const arr = Array.isArray(data) ? data : data?.results || data?.items || data?.data || [];
        setTagOptions(arr);
      } catch { setTagOptions([]); }
    };
    const loadClientTypes = async () => {
      try {
        const data = await catalogService.clientTypes.getAll({ active_only: true });
        const arr = Array.isArray(data) ? data : data?.results || data?.items || data?.data || [];
        setClientTypeOptions(arr);
      } catch { setClientTypeOptions([]); }
    };
    const loadSalespeople = async () => {
      try {
        const data = await catalogService.salespeople.getAll({ active_only: true });
        const arr = Array.isArray(data) ? data : data?.results || data?.items || data?.data || [];
        setSalespersonOptions(arr);
      } catch { setSalespersonOptions([]); }
    };
    const loadAddressTypes = async () => {
      try {
        const data = await catalogService.addressTypes.getAll({ active_only: true });
        const arr = Array.isArray(data) ? data : data?.results || data?.items || data?.data || [];
        setAddressTypeOptions(arr);
      } catch { setAddressTypeOptions([]); }
    };
    const loadPaymentConditions = async () => {
      const toArr = (data) => (Array.isArray(data) ? data : data?.results || data?.items || data?.data || []);
      try {
        const [pm, cd, bp, ap] = await Promise.all([
          catalogService.paymentMethods.getAll({ active_only: true }),
          catalogService.creditDays.getAll({ active_only: true }),
          catalogService.balancePayments.getAll({ active_only: true }),
          catalogService.advancePayments.getAll({ active_only: true }),
        ]);
        setPaymentMethods(toArr(pm));
        setCreditDaysOptions(toArr(cd));
        setBalancePayments(toArr(bp));
        setAdvancePayments(toArr(ap));
      } catch {
        setPaymentMethods([]);
        setCreditDaysOptions([]);
        setBalancePayments([]);
        setAdvancePayments([]);
      }
    };
    const loadPriceLists = async () => {
      try {
        const data = await catalogService.priceLists.getAll();
        const arr = Array.isArray(data) ? data : data?.results || data?.items || data?.data || [];
        setPriceListOptions(arr);
      } catch { setPriceListOptions([]); }
    };
    const loadDiscountLists = async () => {
      try {
        const data = await catalogService.discountLists.getAll({ active_only: true });
        const arr = Array.isArray(data) ? data : data?.results || data?.items || data?.data || [];
        setDiscountListOptions(arr);
      } catch { setDiscountListOptions([]); }
    };
    const loadCountries = async () => {
      try {
        const data = await catalogService.countries.getAll({ active_only: true });
        const arr = Array.isArray(data) ? data : data?.results || data?.items || data?.data || [];
        setCountryOptions(arr);
      } catch { setCountryOptions([]); }
    };
    const loadStates = async () => {
      try {
        const data = await catalogService.states.getAll({ active_only: true });
        const arr = Array.isArray(data) ? data : data?.results || data?.items || data?.data || [];
        setStateOptions(arr);
      } catch { setStateOptions([]); }
    };
    const loadStoreFormats = async () => {
      try {
        const data = await catalogService.storeFormats.getAll({ active_only: true });
        const arr = Array.isArray(data) ? data : data?.results || data?.items || data?.data || [];
        setStoreFormatOptions(arr);
      } catch { setStoreFormatOptions([]); }
    };
    const loadFairs = async () => {
      try {
        const data = await catalogService.fairs.getAll({ active_only: true });
        const arr = Array.isArray(data) ? data : data?.results || data?.items || data?.data || [];
        setFairOptions(arr);
      } catch { setFairOptions([]); }
    };
    const loadCategories = async () => {
      try {
        const data = await categoryService.getFlat({ active_only: true });
        const arr = Array.isArray(data) ? data : data?.results || data?.items || data?.data || [];
        setCategoryOptions(arr);
      } catch { setCategoryOptions([]); }
    };
    loadPorts();
    loadTags();
    loadClientTypes();
    loadSalespeople();
    loadAddressTypes();
    loadPaymentConditions();
    loadPriceLists();
    loadDiscountLists();
    loadCountries();
    loadStates();
    loadStoreFormats();
    loadFairs();
    loadCategories();
  }, []);

  useEffect(() => {
    if (!contactToEdit) return;
    const target = contacts.find((c) => String(c.id) === String(contactToEdit));
    if (!target) return;
    loadContactIntoForm(target);
    onContactEditHandled();
  }, [contactToEdit, contacts, onContactEditHandled, loadContactIntoForm]);

  // Company modal helpers
  const filteredCompaniesForModal = useMemo(() => {
    if (!companyModalSearch.trim()) return companies;
    const s = companyModalSearch.toLowerCase();
    return companies.filter((c) =>
      c.name?.toLowerCase().includes(s) || c.razonSocial?.toLowerCase().includes(s)
    );
  }, [companies, companyModalSearch]);

  const handleOpenCompanyModal = () => {
    setTempCompanyIds(formData.companyIds);
    setShowCompanyModal(true);
    setTimeout(() => companyModalSearchRef.current?.focus(), 100);
  };

  const handleSaveCompanies = () => {
    setFormData((prev) => ({ ...prev, companyIds: tempCompanyIds }));
    setShowCompanyModal(false);
    setCompanyModalSearch('');
  };

  // Form submit
  const handleSubmit = async (e) => {
    e.preventDefault();
    const clientErrors = {};
    if (!formData.name) {
      clientErrors.name = { label: 'Razón social', message: 'Ingresá la Razón social: este campo es obligatorio para guardar el contacto.' };
    }
    if (isClient && !formData.fantasyName) {
      clientErrors.fantasy_name = { label: 'Nombre fantasía', message: 'Ingresá el Nombre de fantasía: es obligatorio para Clientes.' };
    }
    if (isSupplier && !formData.taxInformation) {
      clientErrors.tax_information = { label: 'Información fiscal', message: 'Completá la Información fiscal: es obligatoria para Agentes.' };
    }
    if (Object.keys(clientErrors).length > 0) {
      setFormErrors(clientErrors);
      const firstField = Object.keys(clientErrors)[0];
      const errorTab = FIELD_TO_TAB[firstField];
      if (errorTab) setFormActiveTab(errorTab);
      return;
    }
    setFormErrors({});
    try {
      if (editingContactId) {
        await onUpdateContact(editingContactId, { ...formData, lastContact: new Date() });
        setSuccessMessage('Contacto actualizado correctamente.');
        setTimeout(() => setSuccessMessage(''), 3000);
      } else {
        const created = await onAddContact({ ...formData, id: Date.now().toString(), lastContact: new Date() });
        // Si el backend devuelve el contacto creado con su código, mostrarlo en el formulario
        if (created && created.codigoContacto) {
          setFormData(prev => ({ ...prev, codigoContacto: created.codigoContacto }));
          setEditingContactId(created.id);
        } else {
          resetForm();
          setShowForm(false);
        }
      }
    } catch (error) {
      console.error('Error en handleSubmit:', error);
      const fieldErrors = parseFieldErrors(error);
      if (Object.keys(fieldErrors).length > 0) {
        setFormErrors(fieldErrors);
        // Navegar a la pestaña del primer error
        const firstField = Object.keys(fieldErrors)[0];
        const errorTab = FIELD_TO_TAB[firstField];
        if (errorTab) setFormActiveTab(errorTab);
      }
    }
  };

  // Interaction submit
  const handleInteractionSubmit = (contactId) => async () => {
    if (!interactionFormData.summary.trim()) return;
    try {
      const dateTime = new Date(`${interactionFormData.date}T${interactionFormData.time}`);
      await onAddInteraction({
        id: Date.now().toString(), contactId, date: dateTime,
        type: interactionFormData.type, summary: interactionFormData.summary.trim(),
      });
      setInteractionFormData({ type: 'Llamada', summary: '', date: format(new Date(), 'yyyy-MM-dd'), time: format(new Date(), 'HH:mm') });
      setShowInteractionForm(null);
      setSelectedInteractionType(null);
    } catch (error) {
      console.error('Error en handleInteractionSubmit:', error);
    }
  };

  const handleDelete = async (contactId) => {
    if (!window.confirm('¿Deseas eliminar este contacto?')) return;
    try { await onDeleteContact(contactId); } catch {}
  };

  // Address helpers
  const addAddress = () => {
    setFormData((prev) => ({
      ...prev,
      addresses: [...prev.addresses, { ...emptyAddress('BILLING'), country: prev.country || 'Argentina' }],
    }));
  };

  const removeAddress = (index) => {
    setFormData((prev) => ({
      ...prev,
      addresses: prev.addresses.filter((_, i) => i !== index),
    }));
  };

  const applyCountryFromPort = (portId, extra = {}) => {
    const port = ports.find((p) => String(p.id) === String(portId));
    const countryName = port?.country_name || '';
    setFormData((prev) => ({
      ...prev,
      ...extra,
      ...(countryName
        ? {
            country: countryName,
            addresses: prev.addresses.map((a, i) => (i === 0 ? { ...a, country: countryName } : a)),
          }
        : {}),
    }));
  };

  const updateAddressByIndex = (index, field, value) => {
    setFormData((prev) => ({
      ...prev,
      addresses: prev.addresses.map((a, i) =>
        i === index ? { ...a, [field]: value } : a
      ),
    }));
  };

  const toggleSupplierCurrency = (currency) => {
    setFormData((prev) => ({
      ...prev,
      supplierCurrencies: prev.supplierCurrencies.includes(currency)
        ? prev.supplierCurrencies.filter((c) => c !== currency)
        : [...prev.supplierCurrencies, currency],
    }));
  };

  const addDestinationPort = (portId) => {
    const id = String(portId);
    if (!id) return;
    const port = ports.find((p) => String(p.id) === id);
    const countryName = port?.country_name || '';
    setFormData((prev) => {
      const current = (prev.destinationPortIds || []).map(String);
      if (current.includes(id)) return prev;
      return {
        ...prev,
        destinationPortIds: [...current, id],
        ...(countryName
          ? {
              country: countryName,
              addresses: prev.addresses.map((a, i) => (i === 0 ? { ...a, country: countryName } : a)),
            }
          : {}),
      };
    });
  };

  const removeDestinationPort = (portId) => {
    const id = String(portId);
    setFormData((prev) => ({
      ...prev,
      destinationPortIds: (prev.destinationPortIds || []).map(String).filter((x) => x !== id),
    }));
  };

  const selectedFairs = (formData.fairs || '').split(',').map((s) => s.trim()).filter(Boolean);

  const categoryLabel = (c) => (c.full_path || c.name || '').trim();
  const selectedCategories = (formData.categories || '').split(',').map((s) => s.trim()).filter(Boolean);

  const addCategory = (label) => {
    if (!label) return;
    setFormData((prev) => {
      const current = (prev.categories || '').split(',').map((s) => s.trim()).filter(Boolean);
      if (current.includes(label)) return prev;
      return { ...prev, categories: [...current, label].join(', ') };
    });
  };

  const removeCategory = (label) => {
    setFormData((prev) => {
      const current = (prev.categories || '').split(',').map((s) => s.trim()).filter(Boolean);
      return { ...prev, categories: current.filter((c) => c !== label).join(', ') };
    });
  };

  const addFair = (name) => {
    if (!name) return;
    setFormData((prev) => {
      const current = (prev.fairs || '').split(',').map((s) => s.trim()).filter(Boolean);
      if (current.includes(name)) return prev;
      return { ...prev, fairs: [...current, name].join(', ') };
    });
  };

  const removeFair = (name) => {
    setFormData((prev) => {
      const current = (prev.fairs || '').split(',').map((s) => s.trim()).filter(Boolean);
      return { ...prev, fairs: current.filter((f) => f !== name).join(', ') };
    });
  };

  // Filters
  const filteredContacts = useMemo(() => contacts.filter((contact) => {
    const matchesSearch = contact.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      contact.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (contact.codigoContacto || '').toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || contact.status === statusFilter;
    const matchesType = typeFilter === 'all' || contact.contactType === typeFilter;
    const matchesCompany = companyFilter === 'all' ||
      (contact.companies?.some((c) => c.id === parseInt(companyFilter)));
    return matchesSearch && matchesStatus && matchesType && matchesCompany;
  }), [contacts, searchTerm, statusFilter, typeFilter, companyFilter]);



  // Indicators
  const indicators = useMemo(() => {
    const now = new Date();
    const weekStart = startOfWeek(now, { weekStartsOn: 1 });
    const weekEnd = endOfWeek(now, { weekStartsOn: 1 });
    const weeklyIds = new Set(
      contactInteractions
        .filter((i) => isWithinInterval(i.date instanceof Date ? i.date : new Date(i.date), { start: weekStart, end: weekEnd }))
        .map((i) => i.contactId)
    );
    return {
      contactedThisWeek: weeklyIds.size,
      borradorCount: contacts.filter((c) => c.status === 'Borrador').length,
      activoCount: contacts.filter((c) => c.status === 'Activo').length,
      inactivoCount: contacts.filter((c) => c.status === 'Inactivo').length,
    };
  }, [contacts, contactInteractions]);

  const handleExportToExcel = () => {
    const headers = [
      'Tipo Contacto', 'Código', 'Nombre', 'Nombre Fantasía', 'Email', 'Teléfono', 'Móvil',
      'Estado', 'Cargo', 'Comprador', 'País', 'Ciudad', 'Dirección',
      'ID Wechat', 'Sitio WEB', 'Categorías', 'Comentarios',
    ];
    const data = contacts.map((c) => [
      c.contactType || '', c.codigoContacto || '', c.name || '', c.fantasyName || '',
      c.email || '', c.phone || '', c.mobile || '',
      c.contactType === 'CLIENT' ? (c.status || '') : '', c.position || '', c.comprador || '',
      c.country || '', c.city || '', c.address || '',
      c.wechatId || '', c.website || '', c.categories || '', c.comments || '',
    ]);
    const ws = XLSX.utils.aoa_to_sheet([headers, ...data]);
    ws['!cols'] = headers.map(() => ({ wch: 20 }));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Contactos');
    XLSX.writeFile(wb, `contactos_${format(new Date(), 'yyyy-MM-dd_HH-mm-ss')}.xlsx`);
  };

  const isFactory = formData.contactType === 'FACTORY';
  const isSupplier = formData.contactType === 'SUPPLIER';
  const isClient = formData.contactType === 'CLIENT';
  const selectedPaymentMethod = paymentMethods.find(
    (pm) => String(pm.id) === String(formData.paymentMethodId)
  ) || null;
  const tabs = getTabsForType(formData.contactType);

  // ============================================================
  // RENDER: FORM SECTIONS
  // ============================================================

  const renderGeneralTab = () => (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
      <FormSelect label="Tipo de contacto" required error={formErrors.contact_type?.message} value={formData.contactType}
        onChange={(e) => setFormData((p) => ({ ...p, contactType: e.target.value }))}>
        {CONTACT_TYPE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
      </FormSelect>

      <FormInput label="Razón social" required error={formErrors.name?.message} type="text" value={formData.name}
        onChange={(e) => setFormData((p) => ({ ...p, name: e.target.value }))} />

      {isClient && (
        <FormInput label="Nombre fantasía" required error={formErrors.fantasy_name?.message} type="text" value={formData.fantasyName}
          onChange={(e) => setFormData((p) => ({ ...p, fantasyName: e.target.value }))} />
      )}

      <div>
        <label className="block text-xs font-medium text-gray-500 mb-1">Código de contacto</label>
        <input
          type="text"
          value={formData.codigoContacto}
          readOnly
          disabled
          placeholder="Se genera al guardar"
          className={`${INPUT_CLASS} bg-blue-50 text-blue-600 cursor-not-allowed font-mono`}
        />
      </div>

      {!isFactory && (
        <FormSelect label="KAM" value={formData.kam}
          onChange={(e) => setFormData((p) => ({ ...p, kam: e.target.value }))}>
          <option value="">— Sin KAM asignado —</option>
          {salespersonOptions.map((s) => (
            <option key={s.id} value={s.full_name || `${s.first_name} ${s.last_name}`}>
              {s.first_name} {s.last_name}
            </option>
          ))}
        </FormSelect>
      )}

      {!isClient && (
        <FormInput label="Sitio WEB" type="text" value={formData.website}
          placeholder="ejemplo.com"
          onChange={(e) => setFormData((p) => ({ ...p, website: e.target.value }))} />
      )}

      {isClient && (
        <FormSelect label="Tipo de cliente" value={formData.labelType}
          onChange={(e) => setFormData((p) => ({ ...p, labelType: e.target.value }))}>
          <option value="">— Seleccionar —</option>
          {clientTypeOptions.map((t) => <option key={t.id} value={t.name}>{t.name}</option>)}
        </FormSelect>
      )}

      {isClient && formData.labelType !== 'Distribuidor' && (
        <>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Formato de tienda</label>
            <select value={formData.storeFormat}
              onChange={(e) => setFormData((p) => ({ ...p, storeFormat: e.target.value }))}
              className={INPUT_CLASS}>
              <option value="">— Seleccionar —</option>
              {storeFormatOptions.map((sf) => (
                <option key={sf.id} value={sf.name}>{sf.name}</option>
              ))}
              {formData.storeFormat && !storeFormatOptions.find(sf => sf.name === formData.storeFormat) && (
                <option value={formData.storeFormat}>{formData.storeFormat}</option>
              )}
            </select>
          </div>
          <FormInput label="Número de tiendas" type="text" value={formData.storeNumber}
            onChange={(e) => setFormData((p) => ({ ...p, storeNumber: e.target.value }))} />
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Ferias</label>
            <select
              value=""
              onChange={(e) => addFair(e.target.value)}
              className={INPUT_CLASS}
            >
              <option value="">— Seleccionar —</option>
              {fairOptions
                .filter((f) => !selectedFairs.includes(f.name))
                .map((f) => (
                  <option key={f.id} value={f.name}>{f.name}</option>
                ))}
            </select>
            {selectedFairs.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-2">
                {selectedFairs.map((name) => (
                  <span
                    key={name}
                    className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg bg-blue-50 border border-blue-200 text-xs font-medium text-blue-700"
                  >
                    {name}
                    <button
                      type="button"
                      onClick={() => removeFair(name)}
                      className="p-0.5 rounded hover:bg-blue-100"
                      title="Quitar"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                ))}
              </div>
            )}
          </div>
        </>
      )}

      <FormInput label="Tax ID" type="text" value={formData.idNumber}
        onChange={(e) => setFormData((p) => ({ ...p, idNumber: e.target.value }))} />

      {/* Agente oculto por ahora
      {isFactory && (
        <FormSelect label="Agente" value={formData.agentId}
          onChange={(e) => setFormData((p) => ({ ...p, agentId: e.target.value }))}>
          <option value="">Sin agente asignado</option>
          {contacts.filter((c) => c.contactType === 'SUPPLIER').map((c) => (
            <option key={c.id} value={c.id}>
              {c.codigoContacto ? `${c.codigoContacto} — ` : ''}{c.name}
            </option>
          ))}
        </FormSelect>
      )}
      */}

      {isFactory && (
        <FormSelect label="Puerto Fábrica" value={formData.factoryPortId}
          onChange={(e) => applyCountryFromPort(e.target.value, { factoryPortId: e.target.value })}>
          <option value="">Seleccionar puerto</option>
          {ports.map((p) => <option key={p.id} value={p.id}>{p.name}{p.country_name ? ` (${p.country_name})` : ''}</option>)}
        </FormSelect>
      )}

      {(isFactory || isSupplier) && (
        <div className="md:col-span-3">
          <label className="block text-xs font-medium text-gray-500 mb-1">Categoría</label>
          <select
            value=""
            onChange={(e) => addCategory(e.target.value)}
            className={INPUT_CLASS}
          >
            <option value="">Seleccionar</option>
            {categoryOptions
              .filter((c) => c.is_active !== false && !selectedCategories.includes(categoryLabel(c)))
              .map((c) => (
                <option key={c.id} value={categoryLabel(c)}>
                  {'─'.repeat(c.level || 0)} {categoryLabel(c)}
                </option>
              ))}
          </select>
          {selectedCategories.length > 0 && (
            <div className="flex flex-wrap gap-2 mt-2">
              {selectedCategories.map((label) => (
                <span
                  key={label}
                  className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg bg-blue-50 border border-blue-200 text-xs font-medium text-blue-700"
                >
                  {label}
                  <button
                    type="button"
                    onClick={() => removeCategory(label)}
                    className="p-0.5 rounded hover:bg-blue-100"
                    title="Quitar"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </span>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ── Direcciones ────────────────────────────────────── */}
      <div className="md:col-span-3">
        <SectionDivider label="Direcciones" />
        <div className="space-y-3">
          <div className="flex justify-end">
            <button type="button" onClick={addAddress}
              className="px-3 py-1.5 text-xs font-medium text-blue-700 bg-blue-50 border border-blue-200 rounded-lg hover:bg-blue-100 transition-colors flex items-center gap-1">
              <Plus className="w-3.5 h-3.5" /> Agregar dirección
            </button>
          </div>
          {formData.addresses.length === 0 ? (
            <div className="text-center py-6 bg-gray-50 border border-dashed border-gray-200 rounded-lg">
              <MapPinned className="w-8 h-8 text-gray-300 mx-auto mb-1" />
              <p className="text-xs text-gray-400">Sin direcciones cargadas — hacé clic en "Agregar dirección"</p>
            </div>
          ) : (
            <div className="space-y-3">
              {formData.addresses.map((addr, idx) => {
                const typeLabel = addressTypeOptions.length > 0
                  ? (addressTypeOptions.find((t) => t.code === addr.addressType || t.name === addr.addressType)?.name || addr.addressType || 'Dirección')
                  : (ADDRESS_TYPES.find((t) => t.value === addr.addressType)?.label || 'Dirección');
                return (
                  <div key={idx} className="border border-gray-200 rounded-lg px-3 py-2 space-y-2">
                    {/* Línea 1: Tipo · Nombre · Calle · Calle 2 · [X] */}
                    <div className="flex items-end gap-2">
                      <div className="w-36 shrink-0">
                        <label className="block text-xs font-medium text-gray-500 mb-1">Tipo</label>
                        <select value={addr.addressType}
                          onChange={(e) => updateAddressByIndex(idx, 'addressType', e.target.value)}
                          className={INPUT_CLASS}>
                          <option value="">— Tipo —</option>
                          {addressTypeOptions.length > 0
                            ? addressTypeOptions.map((t) => (
                                <option key={t.id} value={t.name}>{t.name}</option>
                              ))
                            : ADDRESS_TYPES.map((t) => (
                                <option key={t.value} value={t.value}>{t.label}</option>
                              ))
                          }
                        </select>
                      </div>
                      {isClient && (
                        <div className="flex-1 min-w-0">
                          <label className="block text-xs font-medium text-gray-500 mb-1">Nombre</label>
                          <input type="text" value={addr.name || ''}
                            onChange={(e) => updateAddressByIndex(idx, 'name', e.target.value)}
                            className={INPUT_CLASS} />
                        </div>
                      )}
                      <div className="flex-[2] min-w-0">
                        <label className="block text-xs font-medium text-gray-500 mb-1">Calle</label>
                        <input type="text" value={addr.street || ''}
                          onChange={(e) => updateAddressByIndex(idx, 'street', e.target.value)}
                          className={INPUT_CLASS} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <label className="block text-xs font-medium text-gray-500 mb-1">Calle 2</label>
                        <input type="text" value={addr.street2 || ''}
                          onChange={(e) => updateAddressByIndex(idx, 'street2', e.target.value)}
                          className={INPUT_CLASS} />
                      </div>
                      <button type="button" onClick={() => removeAddress(idx)}
                        className="mb-0.5 p-1.5 text-red-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors shrink-0"
                        title="Eliminar dirección">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                    {/* Línea 2: País · Provincia · contacto */}
                    <div className="flex items-end gap-2">
                      <div className="w-36 shrink-0">
                        <label className="block text-xs font-medium text-gray-500 mb-1">País</label>
                        <select value={addr.country || ''}
                          onChange={(e) => updateAddressByIndex(idx, 'country', e.target.value)}
                          className={INPUT_CLASS}>
                          <option value="">— País —</option>
                          {countryOptions.map((c) => (
                            <option key={c.id} value={c.name}>{c.name}</option>
                          ))}
                          {addr.country && !countryOptions.find(c => c.name === addr.country) && (
                            <option value={addr.country}>{addr.country}</option>
                          )}
                        </select>
                      </div>
                      <div className="flex-1 min-w-0">
                        <label className="block text-xs font-medium text-gray-500 mb-1">Estado / Provincia</label>
                        <input type="text" value={addr.state || ''}
                          onChange={(e) => updateAddressByIndex(idx, 'state', e.target.value)}
                          className={INPUT_CLASS} />
                      </div>
                      {(isFactory || isSupplier) ? (
                        <>
                          <div className="flex-1 min-w-0">
                            <label className="block text-xs font-medium text-gray-500 mb-1">Teléfono</label>
                            <input type="tel" value={addr.phone || ''}
                              onChange={(e) => updateAddressByIndex(idx, 'phone', e.target.value)}
                              className={INPUT_CLASS} />
                          </div>
                          <div className="flex-1 min-w-0">
                            <label className="block text-xs font-medium text-gray-500 mb-1">Wechat</label>
                            <input type="text" value={addr.wechatId || ''}
                              onChange={(e) => updateAddressByIndex(idx, 'wechatId', e.target.value)}
                              className={INPUT_CLASS} />
                          </div>
                          <div className="flex-1 min-w-0">
                            <label className="block text-xs font-medium text-gray-500 mb-1">QQ</label>
                            <input type="text" value={addr.qq || ''}
                              onChange={(e) => updateAddressByIndex(idx, 'qq', e.target.value)}
                              className={INPUT_CLASS} />
                          </div>
                        </>
                      ) : (
                        <>
                          <div className="flex-1 min-w-0">
                            <label className="block text-xs font-medium text-gray-500 mb-1">Correo electrónico</label>
                            <input type="email" value={addr.email || ''}
                              onChange={(e) => updateAddressByIndex(idx, 'email', e.target.value)}
                              className={INPUT_CLASS} />
                          </div>
                          <div className="flex-1 min-w-0">
                            <label className="block text-xs font-medium text-gray-500 mb-1">Teléfono</label>
                            <input type="tel" value={addr.phone || ''}
                              onChange={(e) => updateAddressByIndex(idx, 'phone', e.target.value)}
                              className={INPUT_CLASS} />
                          </div>
                        </>
                      )}
                      <div className="w-8 shrink-0" />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      <div className="md:col-span-3">
        <FormTextarea label="Comentario" rows={2} value={formData.description}
          onChange={(e) => setFormData((p) => ({ ...p, description: e.target.value }))} />
      </div>

      {/* ── Personas de contacto ─────────────────────────── */}
      <div className="md:col-span-3">
        <SectionDivider label="Personas de contacto" />
        <div className="space-y-2">
          <div className="flex justify-end">
            <button type="button" onClick={addContactPerson}
              className="px-3 py-1.5 text-xs font-medium text-blue-700 bg-blue-50 border border-blue-200 rounded-lg hover:bg-blue-100 transition-colors flex items-center gap-1">
              <Plus className="w-3.5 h-3.5" /> Agregar persona
            </button>
          </div>
          {formData.contactPersons.length === 0 ? (
            <div className="text-center py-6 bg-gray-50 border border-dashed border-gray-200 rounded-lg">
              <Users className="w-8 h-8 text-gray-300 mx-auto mb-1" />
              <p className="text-xs text-gray-400">Sin personas de contacto — hacé clic en "Agregar persona"</p>
            </div>
          ) : (
            <div className="space-y-2">
              {formData.contactPersons.map((person, idx) => (
                <div key={idx} className="border border-gray-200 rounded-lg px-3 py-2 flex items-end gap-2">
                  <div className="flex-1 min-w-0">
                    <label className="block text-xs font-medium text-gray-500 mb-1">Nombre <span className="text-red-500">*</span></label>
                    <input type="text" value={person.name}
                      onChange={(e) => updateContactPerson(idx, 'name', e.target.value)}
                      className={INPUT_CLASS} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <label className="block text-xs font-medium text-gray-500 mb-1">Rol</label>
                    <input type="text" value={person.position}
                      onChange={(e) => updateContactPerson(idx, 'position', e.target.value)}
                      className={INPUT_CLASS} />
                  </div>
                  {isClient && (
                    <div className="flex-1 min-w-0">
                      <label className="block text-xs font-medium text-gray-500 mb-1">Teléfono</label>
                      <input type="tel" value={person.phone || ''} placeholder="+54 11 1234-5678"
                        onChange={(e) => updateContactPerson(idx, 'phone', e.target.value)}
                        className={INPUT_CLASS} />
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <label className="block text-xs font-medium text-gray-500 mb-1">Correo electrónico</label>
                    <input type="email" value={person.email}
                      onChange={(e) => updateContactPerson(idx, 'email', e.target.value)}
                      className={INPUT_CLASS} />
                  </div>
                  {isClient && (
                    <div className="flex-1 min-w-0">
                      <label className="block text-xs font-medium text-gray-500 mb-1">Comentarios</label>
                      <input type="text" value={person.categories}
                        onChange={(e) => updateContactPerson(idx, 'categories', e.target.value)}
                        className={INPUT_CLASS} />
                    </div>
                  )}
                  <button type="button" onClick={() => removeContactPerson(idx)}
                    className="p-2 text-red-400 hover:bg-red-50 rounded-lg transition-colors flex-shrink-0 mb-0.5" title="Eliminar">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {(isClient || isSupplier) && (
        <div className="md:col-span-3">
          <SectionDivider label="Info envío" />
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FormInput label="Notify Party" type="text" value={formData.notifyParty}
                onChange={(e) => setFormData((p) => ({ ...p, notifyParty: e.target.value }))} />
              <FormInput label="Consignee" type="text" value={formData.consignee}
                onChange={(e) => setFormData((p) => ({ ...p, consignee: e.target.value }))} />
              <FormInput label="ID del transitario" type="text" value={formData.transitId}
                onChange={(e) => setFormData((p) => ({ ...p, transitId: e.target.value }))} />

              {isSupplier && (
                <FormSelect label="Puerto de carga" value={formData.loadPortId}
                  onChange={(e) => applyCountryFromPort(e.target.value, { loadPortId: e.target.value })}>
                  <option value="">Seleccionar puerto</option>
                  {ports.map((p) => <option key={p.id} value={p.id}>{p.name}{p.country_name ? ` (${p.country_name})` : ''}</option>)}
                </FormSelect>
              )}
            </div>

            {isClient && (
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">Puerto destino</label>
                {ports.length === 0 ? (
                  <p className="text-xs text-gray-400">No hay puertos cargados. Agregalos en Configuración → Puertos.</p>
                ) : (
                  <>
                    <select
                      value=""
                      onChange={(e) => addDestinationPort(e.target.value)}
                      className={INPUT_CLASS}
                    >
                      <option value="">Seleccionar puerto</option>
                      {ports
                        .filter((p) => !(formData.destinationPortIds || []).map(String).includes(String(p.id)))
                        .map((p) => (
                          <option key={p.id} value={p.id}>
                            {p.name}{p.country_name ? ` (${p.country_name})` : ''}
                          </option>
                        ))}
                    </select>
                    {(formData.destinationPortIds || []).length > 0 && (
                      <div className="flex flex-wrap gap-2 mt-2">
                        {(formData.destinationPortIds || []).map((id) => {
                          const port = ports.find((p) => String(p.id) === String(id));
                          if (!port) return null;
                          return (
                            <span
                              key={id}
                              className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg bg-blue-50 border border-blue-200 text-xs font-medium text-blue-700"
                            >
                              {port.name}{port.country_name ? ` (${port.country_name})` : ''}
                              <button
                                type="button"
                                onClick={() => removeDestinationPort(id)}
                                className="p-0.5 rounded hover:bg-blue-100"
                                title="Quitar"
                              >
                                <X className="w-3 h-3" />
                              </button>
                            </span>
                          );
                        })}
                      </div>
                    )}
                  </>
                )}
              </div>
            )}

            <div className="grid grid-cols-1 gap-4">
              <FormTextarea label="Requisitos" rows={2} value={formData.requirements}
                onChange={(e) => setFormData((p) => ({ ...p, requirements: e.target.value }))} />
              <FormTextarea label="Instrucciones de envío" rows={2} value={formData.shippingInstructions}
                onChange={(e) => setFormData((p) => ({ ...p, shippingInstructions: e.target.value }))} />
            </div>
          </div>
        </div>
      )}

      <div className="md:col-span-3">
        <SectionDivider label="Financiero" />
        <div className="space-y-4">
          {isClient && (
            <div className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                <FormSelect
                  label="Forma de pago"
                  value={formData.paymentMethodId}
                  onChange={(e) => {
                    const id = e.target.value;
                    const pm = paymentMethods.find((p) => String(p.id) === String(id));
                    setFormData((prev) => ({
                      ...prev,
                      paymentMethodId: id,
                      advancePaymentId: pm?.uses_advance ? prev.advancePaymentId : '',
                      balancePaymentId: pm?.uses_balance_payment ? prev.balancePaymentId : '',
                    }));
                  }}
                >
                  <option value="">Seleccionar</option>
                  {paymentMethods.map((pm) => (
                    <option key={pm.id} value={pm.id}>{pm.name}</option>
                  ))}
                </FormSelect>
                <FormSelect label="Días de crédito" value={formData.creditDaysId}
                  onChange={(e) => setFormData((p) => ({ ...p, creditDaysId: e.target.value }))}>
                  <option value="">Seleccionar</option>
                  {creditDaysOptions.map((cd) => (
                    <option key={cd.id} value={cd.id}>{cd.days} días</option>
                  ))}
                </FormSelect>
                <FormSelect label="Lista de precio" value={formData.priceList}
                  onChange={(e) => setFormData((p) => ({ ...p, priceList: e.target.value }))}>
                  <option value="">Sin lista asignada</option>
                  {priceListOptions.map((pl) => (
                    <option key={pl.id} value={pl.name}>
                      {pl.name}{pl.code ? ` (${pl.code})` : ''}
                    </option>
                  ))}
                </FormSelect>
                <FormSelect label="Descuento" value={formData.discountListId}
                  onChange={(e) => setFormData((p) => ({ ...p, discountListId: e.target.value }))}>
                  <option value="">Sin descuento</option>
                  {discountListOptions.map((dl) => (
                    <option key={dl.id} value={dl.id}>{dl.name}{dl.currency ? ` (${dl.currency})` : ''}</option>
                  ))}
                </FormSelect>
              </div>

              {(selectedPaymentMethod?.uses_advance || selectedPaymentMethod?.uses_balance_payment) && (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                  {selectedPaymentMethod?.uses_advance && (
                    <FormSelect label="Anticipo" value={formData.advancePaymentId}
                      onChange={(e) => setFormData((p) => ({ ...p, advancePaymentId: e.target.value }))}>
                      <option value="">Seleccionar</option>
                      {advancePayments.map((ap) => (
                        <option key={ap.id} value={ap.id}>
                          {ap.advance_percentage != null ? `${ap.advance_percentage}%` : ap.name}
                        </option>
                      ))}
                    </FormSelect>
                  )}
                  {selectedPaymentMethod?.uses_balance_payment && (
                    <FormSelect label="Balance Payment" value={formData.balancePaymentId}
                      onChange={(e) => setFormData((p) => ({ ...p, balancePaymentId: e.target.value }))}>
                      <option value="">Seleccionar</option>
                      {balancePayments.map((bp) => (
                        <option key={bp.id} value={bp.id}>{bp.name}</option>
                      ))}
                    </FormSelect>
                  )}
                </div>
              )}
            </div>
          )}

          {isSupplier && (
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FormInput label="Referencia" type="text" value={formData.reference}
                  onChange={(e) => setFormData((p) => ({ ...p, reference: e.target.value }))} />
                <FormTextarea label="Información fiscal" required error={formErrors.tax_information?.message} rows={2} value={formData.taxInformation}
                  onChange={(e) => setFormData((p) => ({ ...p, taxInformation: e.target.value }))} />
                <FormInput label="Término de pago" type="text" value={formData.paymentTerm}
                  onChange={(e) => setFormData((p) => ({ ...p, paymentTerm: e.target.value }))} />
                <FormInput label="Comisión (%)" type="number" step="0.01" min="0" max="100"
                  value={formData.commissionPercentage}
                  onChange={(e) => setFormData((p) => ({ ...p, commissionPercentage: e.target.value }))} />
                <FormInput label="Recordatorio de recibo" type="text" value={formData.receiptReminder}
                  onChange={(e) => setFormData((p) => ({ ...p, receiptReminder: e.target.value }))} />
                <FormSelect label="Descuento" value={formData.discountListId}
                  onChange={(e) => setFormData((p) => ({ ...p, discountListId: e.target.value }))}>
                  <option value="">Sin descuento</option>
                  {discountListOptions.map((dl) => (
                    <option key={dl.id} value={dl.id}>{dl.name}{dl.currency ? ` (${dl.currency})` : ''}</option>
                  ))}
                </FormSelect>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-2">Divisas proveedor</label>
                <div className="flex flex-wrap gap-2">
                  {SUPPLIER_CURRENCY_OPTIONS.map((c) => (
                    <button key={c} type="button" onClick={() => toggleSupplierCurrency(c)}
                      className={`px-3 py-1.5 rounded-lg border text-xs font-medium transition-colors ${
                        formData.supplierCurrencies.includes(c)
                          ? 'bg-blue-50 border-blue-300 text-blue-700'
                          : 'bg-white border-gray-300 text-gray-600 hover:bg-gray-50'
                      }`}>
                      {c}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {isFactory && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FormSelect label="Término de pago" value={formData.paymentMethodId}
                onChange={(e) => setFormData((p) => ({ ...p, paymentMethodId: e.target.value }))}>
                <option value="">Seleccionar</option>
                {paymentMethods.map((pm) => (
                  <option key={pm.id} value={pm.id}>{pm.name}</option>
                ))}
              </FormSelect>
            </div>
          )}

          {(isSupplier || isFactory) && (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FormInput label="Banco" type="text" value={formData.bank}
                  onChange={(e) => setFormData((p) => ({ ...p, bank: e.target.value }))} />
                <FormInput label="Número de cuenta" type="text" value={formData.accountNumber}
                  onChange={(e) => setFormData((p) => ({ ...p, accountNumber: e.target.value }))} />
              </div>

              {isSupplier && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <FormInput label="Beneficiary Account Name" type="text" value={formData.beneficiaryAccountName}
                    onChange={(e) => setFormData((p) => ({ ...p, beneficiaryAccountName: e.target.value }))} />
                  <FormInput label="Beneficiary Bank Address" type="text" value={formData.beneficiaryBankAddress}
                    onChange={(e) => setFormData((p) => ({ ...p, beneficiaryBankAddress: e.target.value }))} />
                  <FormInput label="Routing/ABA Number" type="text" value={formData.routingAba}
                    onChange={(e) => setFormData((p) => ({ ...p, routingAba: e.target.value }))} />
                  <FormInput label="SWIFT/BIC Code" type="text" value={formData.swiftBic}
                    onChange={(e) => setFormData((p) => ({ ...p, swiftBic: e.target.value }))} />
                </div>
              )}

              {isFactory && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <FormInput label="Beneficiary Account Name" type="text" value={formData.beneficiaryAccountName}
                    onChange={(e) => setFormData((p) => ({ ...p, beneficiaryAccountName: e.target.value }))} />
                </div>
              )}
            </>
          )}

          <FormTextarea label="Comentarios" rows={3} value={formData.comments}
            onChange={(e) => setFormData((p) => ({ ...p, comments: e.target.value }))} />
        </div>
      </div>
    </div>
  );

  const emptyPerson = () => ({ name: '', area: '', position: '', email: '', phone: '', wechatId: '', categories: '' });

  const addContactPerson = () => {
    setFormData((p) => ({ ...p, contactPersons: [...p.contactPersons, emptyPerson()] }));
  };

  const updateContactPerson = (index, field, value) => {
    setFormData((p) => ({
      ...p,
      contactPersons: p.contactPersons.map((cp, i) => i === index ? { ...cp, [field]: value } : cp),
    }));
  };

  const removeContactPerson = (index) => {
    setFormData((p) => ({
      ...p,
      contactPersons: p.contactPersons.filter((_, i) => i !== index),
    }));
  };

  const renderContactosTab = () => (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h4 className="text-sm font-semibold text-gray-700">Personas de contacto</h4>
        <button type="button" onClick={addContactPerson}
          className="px-3 py-1.5 text-xs font-medium text-blue-700 bg-blue-50 border border-blue-200 rounded-lg hover:bg-blue-100 transition-colors flex items-center gap-1">
          <Plus className="w-3.5 h-3.5" /> Agregar contacto
        </button>
      </div>

      {formData.contactPersons.length === 0 ? (
        <div className="text-center py-8 bg-gray-50 border border-dashed border-gray-300 rounded-lg">
          <Users className="w-10 h-10 text-gray-300 mx-auto mb-2" />
          <p className="text-sm text-gray-500">Sin personas de contacto</p>
          <p className="text-xs text-gray-400 mt-1">Agregá las personas con las que te comunicás</p>
        </div>
      ) : (
        <div className="space-y-4">
          {formData.contactPersons.map((person, idx) => (
            <div key={idx} className="border border-gray-200 rounded-lg">
              <div className="px-4 py-2.5 bg-gray-50 border-b border-gray-200 rounded-t-lg flex items-center justify-between">
                <h5 className="text-sm font-medium text-gray-700 flex items-center gap-2">
                  <User className="w-4 h-4 text-gray-400" />
                  {person.name || `Contacto ${idx + 1}`}
                </h5>
                <button type="button" onClick={() => removeContactPerson(idx)}
                  className="p-1 text-red-500 hover:bg-red-50 rounded transition-colors" title="Eliminar contacto">
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
              <div className="p-4 grid grid-cols-1 md:grid-cols-3 gap-3">
                <FormInput label="Nombre" required type="text" value={person.name}
                  onChange={(e) => updateContactPerson(idx, 'name', e.target.value)} />
                <FormInput label="Rol" type="text" value={person.position}
                  onChange={(e) => updateContactPerson(idx, 'position', e.target.value)} />
                <FormInput label="Correo electrónico" type="email" value={person.email}
                  onChange={(e) => updateContactPerson(idx, 'email', e.target.value)} />
                <FormInput label="Teléfono" type="tel" value={person.phone || ''}
                  placeholder="+54 11 1234-5678"
                  onChange={(e) => updateContactPerson(idx, 'phone', e.target.value)} />
                {isFactory && (
                  <FormInput label="Categorías" type="text" value={person.categories}
                    onChange={(e) => updateContactPerson(idx, 'categories', e.target.value)} />
                )}
                {!isFactory && (
                  <FormInput label="Comentarios" type="text" value={person.categories}
                    onChange={(e) => updateContactPerson(idx, 'categories', e.target.value)} />
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  const renderDireccionesTab = () => (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h4 className="text-sm font-semibold text-gray-700">Direcciones</h4>
        <button type="button" onClick={addAddress}
          className="px-3 py-1.5 text-xs font-medium text-blue-700 bg-blue-50 border border-blue-200 rounded-lg hover:bg-blue-100 transition-colors flex items-center gap-1">
          <Plus className="w-3.5 h-3.5" /> Agregar dirección
        </button>
      </div>

      {formData.addresses.length === 0 ? (
        <div className="text-center py-8 bg-gray-50 border border-dashed border-gray-300 rounded-lg">
          <MapPinned className="w-10 h-10 text-gray-300 mx-auto mb-2" />
          <p className="text-sm text-gray-500">Sin direcciones cargadas</p>
          <p className="text-xs text-gray-400 mt-1">Agregá las direcciones del contacto</p>
        </div>
      ) : (
        <div className="space-y-4">
          {formData.addresses.map((addr, idx) => {
            const typeLabel = addressTypeOptions.length > 0
              ? (addressTypeOptions.find((t) => t.code === addr.addressType || t.name === addr.addressType)?.name || addr.addressType || 'Dirección')
              : (ADDRESS_TYPES.find((t) => t.value === addr.addressType)?.label || 'Dirección');
            return (
              <div key={idx} className="border border-gray-200 rounded-lg px-3 py-2 space-y-2">
                {/* Línea 1: Tipo · Nombre · Calle · Calle 2 · [X] */}
                <div className="flex items-end gap-2">
                  <div className="w-36 shrink-0">
                    <label className="block text-xs font-medium text-gray-500 mb-1">Tipo</label>
                    <select value={addr.addressType}
                      onChange={(e) => updateAddressByIndex(idx, 'addressType', e.target.value)}
                      className={INPUT_CLASS}>
                      <option value="">— Tipo —</option>
                      {addressTypeOptions.length > 0
                        ? addressTypeOptions.map((t) => (
                            <option key={t.id} value={t.name}>{t.name}</option>
                          ))
                        : ADDRESS_TYPES.map((t) => (
                            <option key={t.value} value={t.value}>{t.label}</option>
                          ))
                      }
                    </select>
                  </div>
                  <div className="flex-1 min-w-0">
                    <label className="block text-xs font-medium text-gray-500 mb-1">Nombre</label>
                    <input type="text" value={addr.name || ''}
                      onChange={(e) => updateAddressByIndex(idx, 'name', e.target.value)}
                      className={INPUT_CLASS} />
                  </div>
                  <div className="flex-[2] min-w-0">
                    <label className="block text-xs font-medium text-gray-500 mb-1">Calle</label>
                    <input type="text" value={addr.street || ''}
                      onChange={(e) => updateAddressByIndex(idx, 'street', e.target.value)}
                      className={INPUT_CLASS} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <label className="block text-xs font-medium text-gray-500 mb-1">Calle 2</label>
                    <input type="text" value={addr.street2 || ''}
                      onChange={(e) => updateAddressByIndex(idx, 'street2', e.target.value)}
                      className={INPUT_CLASS} />
                  </div>
                  <button type="button" onClick={() => removeAddress(idx)}
                    className="mb-0.5 p-1.5 text-red-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors shrink-0"
                    title="Eliminar dirección">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
                {/* Línea 2: País · Estado · Correo · Teléfono */}
                <div className="flex items-end gap-2">
                  <div className="w-36 shrink-0">
                    <label className="block text-xs font-medium text-gray-500 mb-1">País</label>
                    <select value={addr.country || ''}
                      onChange={(e) => updateAddressByIndex(idx, 'country', e.target.value)}
                      className={INPUT_CLASS}>
                      <option value="">— País —</option>
                      {countryOptions.map((c) => (
                        <option key={c.id} value={c.name}>{c.name}</option>
                      ))}
                      {addr.country && !countryOptions.find(c => c.name === addr.country) && (
                        <option value={addr.country}>{addr.country}</option>
                      )}
                    </select>
                  </div>
                  <div className="flex-1 min-w-0">
                    <label className="block text-xs font-medium text-gray-500 mb-1">Estado / Provincia</label>
                    <select value={addr.state || ''}
                      onChange={(e) => updateAddressByIndex(idx, 'state', e.target.value)}
                      className={INPUT_CLASS}>
                      <option value="">— Provincia —</option>
                      {stateOptions.map((s) => (
                        <option key={s.id} value={s.name}>{s.name}</option>
                      ))}
                      {addr.state && !stateOptions.find(s => s.name === addr.state) && (
                        <option value={addr.state}>{addr.state}</option>
                      )}
                    </select>
                  </div>
                  <div className="flex-1 min-w-0">
                    <label className="block text-xs font-medium text-gray-500 mb-1">Correo electrónico</label>
                    <input type="email" value={addr.email || ''}
                      onChange={(e) => updateAddressByIndex(idx, 'email', e.target.value)}
                      className={INPUT_CLASS} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <label className="block text-xs font-medium text-gray-500 mb-1">Teléfono</label>
                    <input type="tel" value={addr.phone || ''}
                      onChange={(e) => updateAddressByIndex(idx, 'phone', e.target.value)}
                      className={INPUT_CLASS} />
                  </div>
                  <div className="w-8 shrink-0" />
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );

  const renderOtraInfoTab = () => (
    <div className="space-y-4">
      <FormTextarea label="Comentarios generales" rows={3} value={formData.comments}
        onChange={(e) => setFormData((p) => ({ ...p, comments: e.target.value }))} />
    </div>
  );

  const renderHistorialTab = () => {
    const contactId = editingContactId;
    if (!contactId) {
      return (
        <div className="text-center py-8">
          <History className="w-10 h-10 text-gray-300 mx-auto mb-2" />
          <p className="text-sm text-gray-500">Guardá el contacto primero para agregar interacciones</p>
        </div>
      );
    }

    const interactions = contactInteractions
      .filter((i) => String(i.contactId) === String(contactId))
      .sort((a, b) => new Date(b.date) - new Date(a.date));

    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h4 className="text-sm font-semibold text-gray-700">Historial de comunicación</h4>
          {!showInteractionForm && (
            <div className="flex gap-1">
              {INTERACTION_TYPES.map((t) => {
                const Icon = t.icon;
                return (
                  <button key={t.value} type="button"
                    onClick={() => { setSelectedInteractionType(t.value); setInteractionFormData({ type: t.value, summary: '', date: format(new Date(), 'yyyy-MM-dd'), time: format(new Date(), 'HH:mm') }); setShowInteractionForm(contactId); }}
                    className={`p-1.5 rounded-lg border text-xs transition-colors hover:bg-${t.color}-50 border-gray-200`}
                    title={t.label}>
                    <Icon className={`w-3.5 h-3.5 text-${t.color}-500`} />
                  </button>
                );
              })}
            </div>
          )}
        </div>

        <AnimatePresence>
          {showInteractionForm === contactId && (
            <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }}>
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 space-y-3">
                <div className="flex items-center gap-2">
                  <select value={interactionFormData.type} onChange={(e) => setInteractionFormData((p) => ({ ...p, type: e.target.value }))}
                    className="h-8 px-2 text-xs border border-gray-300 rounded-lg bg-white">
                    {INTERACTION_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                  </select>
                  <input type="date" value={interactionFormData.date} onChange={(e) => setInteractionFormData((p) => ({ ...p, date: e.target.value }))}
                    className="h-8 px-2 text-xs border border-gray-300 rounded-lg bg-white" />
                  <input type="time" value={interactionFormData.time} onChange={(e) => setInteractionFormData((p) => ({ ...p, time: e.target.value }))}
                    className="h-8 px-2 text-xs border border-gray-300 rounded-lg bg-white" />
                </div>
                <textarea value={interactionFormData.summary} onChange={(e) => setInteractionFormData((p) => ({ ...p, summary: e.target.value }))}
                  placeholder="Resumen de la interacción..." rows={2}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg bg-white resize-none focus:outline-none focus:ring-2 focus:ring-blue-500/30" />
                <div className="flex gap-2 justify-end">
                  <button type="button" onClick={() => { setShowInteractionForm(null); setSelectedInteractionType(null); }}
                    className="px-3 py-1.5 text-xs text-gray-600 bg-white border border-gray-300 rounded-lg hover:bg-gray-50">
                    Cancelar
                  </button>
                  <button type="button" onClick={handleInteractionSubmit(contactId)} className="px-3 py-1.5 text-xs font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 flex items-center gap-1">
                    <Send className="w-3 h-3" /> Registrar
                  </button>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {interactions.length === 0 ? (
          <div className="text-center py-6 bg-gray-50 border border-dashed border-gray-200 rounded-lg">
            <Clock className="w-8 h-8 text-gray-300 mx-auto mb-1" />
            <p className="text-xs text-gray-400">Sin interacciones registradas</p>
          </div>
        ) : (
          <div className="border border-gray-200 rounded-lg overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Fecha</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Tipo</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Resumen</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {interactions.map((interaction) => {
                  const typeInfo = INTERACTION_TYPES.find((t) => t.value === interaction.type);
                  const Icon = typeInfo?.icon || MessageSquare;
                  return (
                    <tr key={interaction.id} className="hover:bg-gray-50">
                      <td className="px-3 py-2 text-xs text-gray-600 whitespace-nowrap">
                        {format(interaction.date instanceof Date ? interaction.date : new Date(interaction.date), 'dd/MM/yyyy HH:mm')}
                      </td>
                      <td className="px-3 py-2">
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-${typeInfo?.color || 'gray'}-50 text-${typeInfo?.color || 'gray'}-700`}>
                          <Icon className="w-3 h-3" /> {interaction.type}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-xs text-gray-700">{interaction.summary}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    );
  };

  const renderActiveTab = () => {
    switch (formActiveTab) {
      case 'general': return renderGeneralTab();
      case 'otra': return renderOtraInfoTab();
      case 'historial': return renderHistorialTab();
      default: return renderGeneralTab();
    }
  };

  // ============================================================
  // MAIN RENDER
  // ============================================================
  return (
    <div className="space-y-4">
      {showImporter ? (
        <>
          <div className="flex items-center gap-3 mb-2">
            <button onClick={() => setShowImporter(false)} className="px-4 py-2 text-sm font-medium text-gray-600 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors flex items-center gap-2">
              <ChevronLeft className="w-4 h-4" /> Volver a Contactos
            </button>
            <span className="text-sm text-gray-400">/</span>
            <span className="text-sm font-medium text-gray-700">Importar Contactos</span>
          </div>
          <ContactImporter onImportContacts={onImportContacts} ports={ports} onClose={() => setShowImporter(false)} />
        </>
      ) : showForm ? (
        <>
          {/* FORM VIEW */}
          <div className="flex items-center gap-3 mb-2">
            <button onClick={() => { setShowForm(false); resetForm(); }} className="px-4 py-2 text-sm font-medium text-gray-600 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors flex items-center gap-2">
              <ChevronLeft className="w-4 h-4" /> Volver
            </button>
            <span className="text-sm text-gray-400">/</span>
            <span className="text-sm font-medium text-gray-700">{editingContactId ? 'Editar Contacto' : 'Nuevo Contacto'}</span>
            {editingContactId && formData.name && (
              <>
                <span className="text-sm text-gray-400">/</span>
                <span className="text-sm font-bold text-gray-900 uppercase tracking-wide">{formData.name}</span>
              </>
            )}
            {formData.fantasyName && (
              <>
                <span className="text-sm text-gray-400">/</span>
                <span className="text-sm font-semibold text-blue-700">{formData.fantasyName}</span>
              </>
            )}
            {formData.codigoContacto && (
              <span className="ml-auto px-2.5 py-0.5 bg-gray-100 text-gray-600 text-xs font-mono rounded">{formData.codigoContacto}</span>
            )}
            {isClient && (
              <select
                value={formData.status}
                onChange={(e) => setFormData((p) => ({ ...p, status: e.target.value }))}
                className={`${formData.codigoContacto ? '' : 'ml-auto'} h-8 px-3 text-xs font-semibold border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500 transition-colors ${
                  formData.status === 'Activo'   ? 'bg-green-50  border-green-300  text-green-700'  :
                  formData.status === 'Inactivo' ? 'bg-gray-100  border-gray-300   text-gray-600'   :
                                                   'bg-amber-50  border-amber-300  text-amber-700'
                }`}
              >
                {STATUS_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            )}
          </div>

          <form onSubmit={handleSubmit} autoComplete="off" className="bg-white border border-gray-200 rounded-xl shadow-sm">
            {/* Tab bar */}
            <div className="border-b border-gray-200 px-4 py-0 flex gap-0 overflow-x-auto">
              {tabs.map((tab) => {
                const Icon = tab.icon;
                const active = formActiveTab === tab.id;
                const tabHasError = Object.keys(formErrors).some(
                  f => FIELD_TO_TAB[f] === tab.id
                );
                return (
                  <button key={tab.id} type="button" onClick={() => setFormActiveTab(tab.id)}
                    className={`relative flex items-center gap-1.5 px-4 py-3 text-xs font-medium border-b-2 transition-colors whitespace-nowrap ${
                      active
                        ? 'border-blue-600 text-blue-600'
                        : tabHasError
                          ? 'border-transparent text-red-500 hover:text-red-700 hover:border-red-300'
                          : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}>
                    <Icon className="w-3.5 h-3.5" />
                    {tab.label}
                    {tabHasError && (
                      <span className="absolute top-2 right-1 w-2 h-2 bg-red-500 rounded-full" />
                    )}
                  </button>
                );
              })}
            </div>

            {/* Panel de errores por campo */}
            {Object.keys(formErrors).length > 0 && (
              <div className="mx-6 mt-4 bg-red-50 border border-red-200 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-2">
                  <svg className="w-4 h-4 text-red-600 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <span className="text-sm font-semibold text-red-800">
                    {Object.keys(formErrors).length === 1
                      ? 'Se encontró 1 error. Corregilo antes de guardar:'
                      : `Se encontraron ${Object.keys(formErrors).length} errores. Corregilos antes de guardar:`}
                  </span>
                </div>
                <ul className="space-y-1">
                  {Object.entries(formErrors).map(([field, { label, message }]) => {
                    const tab = FIELD_TO_TAB[field];
                    const tabLabel = tabs.find(t => t.id === tab)?.label;
                    return (
                      <li key={field} className="flex items-start gap-2 text-sm">
                        <span className="mt-0.5 text-red-400">›</span>
                        <span>
                          {tabLabel && tab !== formActiveTab && (
                            <button
                              type="button"
                              onClick={() => setFormActiveTab(tab)}
                              className="text-red-700 underline underline-offset-2 hover:text-red-900 mr-1 font-medium"
                            >
                              [{tabLabel}]
                            </button>
                          )}
                          <strong className="text-red-800">{label}:</strong>{' '}
                          <span className="text-red-700">{message}</span>
                        </span>
                      </li>
                    );
                  })}
                </ul>
              </div>
            )}

            {/* Tab content */}
            <div className="p-6">
              {renderActiveTab()}
            </div>

            {/* Actions */}
            <div className="border-t border-gray-200 px-6 py-4 flex items-center justify-between bg-gray-50 rounded-b-xl">
              {successMessage ? (
                <span className="text-sm font-medium text-green-700 bg-green-50 border border-green-200 rounded-lg px-4 py-2">
                  ✓ {successMessage}
                </span>
              ) : <span />}
              <div className="flex gap-3">
                <button type="button" onClick={() => { setShowForm(false); resetForm(); }}
                  className="px-5 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors">
                  Cancelar
                </button>
                <button type="submit"
                  className="px-5 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors">
                  {editingContactId ? 'Actualizar' : 'Guardar'}
                </button>
              </div>
            </div>
          </form>
        </>
      ) : (
        <>
          {/* LIST VIEW */}
          <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-6">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-blue-600 rounded-xl">
                  <User className="w-8 h-8 text-white" />
                </div>
                <div>
                  <h1 className="text-2xl font-bold text-gray-800">Gestión de Contactos</h1>
                  <p className="text-gray-500 text-sm">Contactos, empresas y clasificación</p>
                </div>
              </div>
              <div className="flex gap-2 flex-wrap">
                <button onClick={handleExportToExcel}
                  className="px-4 py-2.5 bg-green-600 hover:bg-green-700 text-white rounded-lg font-medium text-sm shadow-sm transition-colors flex items-center gap-2">
                  <Download className="w-4 h-4" /> Exportar
                </button>
                <button onClick={() => setShowImporter(true)}
                  className="px-4 py-2.5 bg-teal-600 hover:bg-teal-700 text-white rounded-lg font-medium text-sm shadow-sm transition-colors flex items-center gap-2">
                  <FileUp className="w-4 h-4" /> Importar
                </button>
                <button onClick={() => { setShowForm(true); resetForm(); }}
                  className="px-4 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium text-sm shadow-sm transition-colors flex items-center gap-2">
                  <Plus className="w-4 h-4" /> Nuevo Contacto
                </button>
              </div>
            </div>
          </div>

          {/* Filters */}
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input type="text" placeholder="Buscar por nombre, email o código..." value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2.5 text-sm bg-white border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500" />
            </div>
            <select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)}
              className="px-3 py-2.5 text-sm bg-white border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/30">
              <option value="all">Todos los tipos</option>
              {CONTACT_TYPE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
            <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}
              className="px-3 py-2.5 text-sm bg-white border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/30">
              <option value="all">Todos los estados</option>
              {STATUS_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
            <select value={companyFilter} onChange={(e) => setCompanyFilter(e.target.value)}
              className="px-3 py-2.5 text-sm bg-white border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/30">
              <option value="all">Todas las empresas</option>
              {companies.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>

          {/* Table */}
          {filteredContacts.length === 0 ? (
            <div className="text-center py-12 bg-white border border-gray-200 rounded-xl shadow-sm">
              <User className="w-12 h-12 text-gray-300 mx-auto mb-3" />
              <h3 className="text-base font-semibold text-gray-500 mb-1">Sin contactos</h3>
              <p className="text-sm text-gray-400">Agregá tu primer contacto para comenzar</p>
            </div>
          ) : (
            <ContactTable
              contacts={filteredContacts}
              onEdit={loadContactIntoForm}
              onDelete={handleDelete}
            />
          )}
        </>
      )}

      {/* Company Selection Modal */}
      <AnimatePresence>
        {showCompanyModal && (
          <motion.div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            onClick={() => { setShowCompanyModal(false); setCompanyModalSearch(''); }}>
            <motion.div className="bg-white rounded-xl shadow-xl w-full max-w-lg max-h-[70vh] flex flex-col mx-4"
              initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.95 }}
              onClick={(e) => e.stopPropagation()}>
              <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200">
                <h3 className="text-base font-semibold text-gray-800">Seleccionar empresa</h3>
                <button onClick={() => { setShowCompanyModal(false); setCompanyModalSearch(''); }}
                  className="p-1.5 hover:bg-gray-100 rounded-lg">
                  <X className="w-4 h-4 text-gray-500" />
                </button>
              </div>
              <div className="px-5 py-3 border-b border-gray-200">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input ref={companyModalSearchRef} type="text" value={companyModalSearch}
                    onChange={(e) => setCompanyModalSearch(e.target.value)}
                    placeholder="Buscar por nombre o razón social..."
                    className="w-full h-9 pl-9 pr-3 text-sm bg-white border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20" />
                </div>
              </div>
              <div className="flex-1 overflow-y-auto p-3">
                {filteredCompaniesForModal.length > 0 ? (
                  <div className="space-y-1">
                    {filteredCompaniesForModal.map((company) => {
                      const isSelected = tempCompanyIds.includes(String(company.id)) || tempCompanyIds.includes(company.id);
                      return (
                        <button key={company.id} type="button"
                          onClick={() => setTempCompanyIds(isSelected ? [] : [String(company.id)])}
                          className={`w-full px-3 py-2.5 text-left flex items-center gap-3 rounded-lg transition-colors border ${
                            isSelected ? 'bg-blue-50 border-blue-200' : 'bg-white border-gray-200 hover:bg-gray-50'
                          }`}>
                          <div className={`w-4 h-4 rounded border flex items-center justify-center flex-shrink-0 ${
                            isSelected ? 'bg-blue-500 border-blue-500' : 'border-gray-300'
                          }`}>
                            {isSelected && <Check className="w-3 h-3 text-white" />}
                          </div>
                          <div className="min-w-0">
                            <div className="text-sm font-medium text-gray-800 truncate">{company.name}</div>
                            {company.razonSocial && <div className="text-xs text-gray-500 truncate">{company.razonSocial}</div>}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <Building className="w-10 h-10 text-gray-300 mx-auto mb-2" />
                    <p className="text-sm text-gray-500">No se encontraron empresas</p>
                  </div>
                )}
              </div>
              <div className="flex items-center justify-end gap-2 px-5 py-3 border-t border-gray-200 bg-gray-50 rounded-b-xl">
                <button type="button" onClick={() => { setShowCompanyModal(false); setCompanyModalSearch(''); }}
                  className="px-3 py-1.5 text-sm text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50">
                  Cancelar
                </button>
                <button type="button" onClick={handleSaveCompanies}
                  className="px-3 py-1.5 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700">
                  Confirmar
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default ContactManager;
